import React, { useMemo } from "react";

import { CardElement } from "@stripe/react-stripe-js";
import type { StripeCardElementOptions } from "@stripe/stripe-js";
import { Box, useTheme, type SxProps, type Theme } from "@mui/material";

import {
  FIELD_RADIUS,
  fieldAccentColor,
  fieldBorderColor,
  fieldFill,
  fieldFocusRing,
} from "@repo/ui";

// Parity with the design-system field, expressed by READING `fieldChrome` rather than by
// re-deriving the same values here. The previous version named the tokens in a comment
// ("primary[100] rest border, brand[600] on focus") and hardcoded them — which is a
// description of a match, not a match: light mode later moved to grey[200] and a white
// fill, and this field silently kept the dark tokens on a light page.
export const creditCardSurfaceSx = (theme: Theme): SxProps<Theme> => ({
  backgroundColor: fieldFill(theme),
  border: `1px solid ${fieldBorderColor(theme)}`,
  borderRadius: `${FIELD_RADIUS}px`,
  // Match the standard @repo/ui Input height (49px) so the card + name-on-card
  // fields line up with every other field.
  minHeight: 49,
  display: "flex",
  alignItems: "center",
  px: 3,
  py: 2,
  transition: "border-color 120ms ease, box-shadow 120ms ease",
  "&:hover": {
    borderColor: fieldAccentColor(theme),
  },
  "&:focus-within": {
    borderColor: fieldAccentColor(theme),
    boxShadow: fieldFocusRing(theme),
  },
  "& .StripeElement": {
    width: "100%",
  },
});

type CreditCardFieldProps = Omit<
  React.ComponentProps<typeof CardElement>,
  "options"
> & {
  options?: StripeCardElementOptions;
  sx?: SxProps<Theme>;
};

export const CreditCardField = ({
  options,
  sx,
  ...rest
}: CreditCardFieldProps) => {
  const theme = useTheme();

  const mergedOptions = useMemo<StripeCardElementOptions>(() => {
    const base = {
      fontSize: "16px",
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.text.primary,
      iconColor: theme.palette.text.disabled,
      "::placeholder": {
        color: theme.palette.text.disabled,
      },
    };

    return {
      ...options,
      style: {
        base: {
          ...base,
          ...(options?.style?.base ?? {}),
        },
        invalid: {
          color: theme.palette.error.main,
          iconColor: theme.palette.error.main,
          ...(options?.style?.invalid ?? {}),
        },
        complete: {
          color: theme.palette.text.primary,
          ...(options?.style?.complete ?? {}),
        },
      },
    };
  }, [
    options,
    theme.palette.error.main,
    theme.palette.text.disabled,
    theme.palette.text.primary,
    theme.typography.fontFamily,
  ]);

  const containerStyles = useMemo(() => creditCardSurfaceSx(theme), [theme]);

  return (
    <Box sx={[containerStyles, sx]}>
      <CardElement options={mergedOptions} {...rest} />
    </Box>
  );
};
